import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error(
      '[ErrorBoundary] Error capturado:',
      error?.message,
      '\nComponent Stack:',
      errorInfo?.componentStack,
      '\nFull Error:',
      error
    );
  }

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || 'Error desconocido';
      const isDev = import.meta.env.DEV;

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-destructive text-2xl">⚠</span>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Algo salió mal
            </h1>
            <p className="text-muted-foreground mb-2">
              Ha ocurrido un error inesperado. Puedes intentar recargar o volver al dashboard.
            </p>
            <p className="text-xs text-muted-foreground/70 mb-6 font-mono bg-muted/50 px-3 py-2 rounded">
              {errorMessage.length > 150 ? errorMessage.slice(0, 150) + '…' : errorMessage}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/"
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                Volver al inicio
              </a>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 border border-border text-foreground rounded-lg font-medium hover:bg-muted transition-colors"
              >
                Recargar página
              </button>
            </div>

            {isDev && this.state.errorInfo && (
              <details className="mt-6 text-left">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                  Stack trace (dev only)
                </summary>
                <pre className="mt-2 text-[10px] text-muted-foreground bg-muted/50 p-3 rounded overflow-auto max-h-48 whitespace-pre-wrap">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
